import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Paper,
  InputAdornment,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
} from "@mui/material";
import { Add, Search, Edit, Delete, UploadFile } from "@mui/icons-material";
import { apiService } from "../api/services/apiService";
import { formatApiDisplayName } from "../utils/apiDisplayName";
import type { API, EntityStatus } from "../types";
import { PaginationControls } from "./common/PaginationControls";
import BulkUploadApiSpecsDialog from "./BulkUploadApiSpecsDialog";
import { useNavigate } from "react-router-dom";

const ITEMS_PER_PAGE = 10;

const ManageApis: React.FC = () => {
  const navigate = useNavigate();

  const [apis, setApis] = useState<API[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const [editApi, setEditApi] = useState<API | null>(null);
  const [saving, setSaving] = useState(false);

  const [deleteApiUuid, setDeleteApiUuid] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);

  /* -------------------- Fetch APIs -------------------- */
  const fetchApis = async () => {
    setLoading(true);
    try {
      setApis(await apiService.getApis());
    } catch (err: any) {
      setError(err.message || "Failed to fetch APIs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApis();
  }, []);

  /* -------------------- Filtering & Pagination -------------------- */
  const filteredApis = apis.filter((api) =>
    api.name.toLowerCase().includes(search.toLowerCase()) ||
      formatApiDisplayName(api.name).toLowerCase().includes(search.toLowerCase()),
  );

  const totalPages = Math.ceil(filteredApis.length / ITEMS_PER_PAGE);
  const paginatedApis = filteredApis.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  /* -------------------- Edit API -------------------- */
  const handleSaveEdit = async () => {
    if (!editApi || !editApi.uuid) return;

    setSaving(true);
    try {
      const updated = await apiService.updateApi(editApi.uuid, {
        name: editApi.name,
        version: editApi.version,
        status: editApi.status as EntityStatus,
      });

      setApis((prev) =>
        prev.map((a) => (a.uuid === updated.uuid ? updated : a)),
      );
      setEditApi(null);
    } catch (err: any) {
      alert(err.message || "Failed to update API");
    } finally {
      setSaving(false);
    }
  };

  /* -------------------- Delete API -------------------- */
  const handleDelete = async () => {
    if (!deleteApiUuid) return;

    setDeleteLoading(true);
    try {
      await apiService.deleteApi(deleteApiUuid);
      setApis((prev) => prev.filter((a) => a.uuid !== deleteApiUuid));
      setDeleteApiUuid(null);
    } catch (err: any) {
      alert(err.message || "Failed to delete api");
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>
          API Specs
        </Typography>

        <Box sx={{ display: "flex", gap: 2 }}>
          <TextField
            size="small"
            placeholder="Search by name"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <Button
            variant="outlined"
            startIcon={<UploadFile />}
            onClick={() => setBulkUploadOpen(true)}
            sx={{ textTransform: "none" }}
          >
            Bulk Upload
          </Button>

          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate("/manage-assets/apis/add")}
          >
            Add API Spec
          </Button>
        </Box>
      </Box>

      <BulkUploadApiSpecsDialog
        open={bulkUploadOpen}
        onClose={() => setBulkUploadOpen(false)}
        onComplete={() => {
          fetchApis();
        }}
      />

      {/* Loading / Error */}
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
          <CircularProgress />
        </Box>
      )}
      {error && <Typography color="error">{error}</Typography>}

      {/* Table */}
      {!loading && !error && (
        <>
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Version</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {paginatedApis.map((api) => (
                  <TableRow key={api.uuid} hover>
                    <TableCell>{formatApiDisplayName(api.name)}</TableCell>
                    <TableCell>{api.version}</TableCell>
                    <TableCell>
                      <Chip
                        label={api.status}
                        size="small"
                        color={api.status === "ACTIVE" ? "success" : "default"}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton onClick={() => setEditApi(api)}>
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => setDeleteApiUuid(api.uuid)}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <PaginationControls
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </>
      )}

      {/* Edit Dialog */}
      <Dialog open={!!editApi} onClose={() => setEditApi(null)} fullWidth>
        <DialogTitle>Edit API</DialogTitle>

        {editApi && (
          <DialogContent>
            <TextField
              label="Name"
              fullWidth
              margin="normal"
              value={editApi.name}
              onChange={(e) => setEditApi({ ...editApi, name: e.target.value })}
            />

            <TextField
              label="Version"
              fullWidth
              margin="normal"
              value={editApi.version}
              onChange={(e) =>
                setEditApi({ ...editApi, version: e.target.value })
              }
            />

            <TextField
              select
              label="Status"
              fullWidth
              margin="normal"
              value={editApi.status}
              onChange={(e) =>
                setEditApi({
                  ...editApi,
                  status: e.target.value as EntityStatus,
                })
              }
            >
              <MenuItem value="ACTIVE">ACTIVE</MenuItem>
              <MenuItem value="INACTIVE">INACTIVE</MenuItem>
            </TextField>
          </DialogContent>
        )}

        <DialogActions>
          <Button onClick={() => setEditApi(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveEdit}
            disabled={saving}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog
        open={!!deleteApiUuid}
        onClose={() => setDeleteApiUuid(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <strong>Delete API?</strong>
        </DialogTitle>
        <DialogContent>Are you sure you want to delete this API?</DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteApiUuid(null)}
            variant="outlined"
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            No
          </Button>
          <Button
            color="error"
            variant="contained"
            sx={{ textTransform: "none", fontWeight: 600 }}
            onClick={handleDelete}
            disabled={deleteLoading}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManageApis;
